import { memo, useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { ChevronDown, ChevronUp, FileCheck, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DocumentChecklistProps {
  installationId: number;
  documents: DocumentStatus;
  compact?: boolean;
}

interface DocumentStatus {
  invoicePaid: boolean;
  evcpApplication: boolean;
  g99Commissioning: boolean;
  mcsCert: boolean;
  eicCert: boolean;
  partP: boolean;
  ownersManual: boolean;
  sld: boolean;
  datasheets: boolean;
  invoices: boolean;
  rams: boolean;
}

interface DocumentItem {
  key: keyof DocumentStatus;
  label: string;
  dbField: string;
  priority: 'high' | 'medium' | 'low';
}

const DOCUMENT_ITEMS: DocumentItem[] = [
  { key: 'invoicePaid', label: 'Invoice paid', dbField: 'checklistInvoicePaid', priority: 'high' },
  { key: 'evcpApplication', label: 'EVCP application', dbField: 'checklistEvcpApplication', priority: 'medium' },
  { key: 'g99Commissioning', label: 'G99 commissioning', dbField: 'checklistG99Commissioning', priority: 'high' },
  { key: 'mcsCert', label: 'MCS Cert', dbField: 'checklistMcsCertification', priority: 'high' },
  { key: 'eicCert', label: 'EIC cert', dbField: 'checklistEicCertificateProvided', priority: 'high' },
  { key: 'partP', label: 'Part P', dbField: 'checklistPartPBuildingRegsCertificate', priority: 'high' },
  { key: 'ownersManual', label: 'Owners Manual', dbField: 'checklistOwnerManual', priority: 'medium' },
  { key: 'sld', label: 'SLD', dbField: 'checklistSld', priority: 'medium' },
  { key: 'datasheets', label: 'Datasheets', dbField: 'checklistDatasheets', priority: 'low' },
  { key: 'invoices', label: 'Invoices', dbField: 'checklistInvoicesProvided', priority: 'medium' },
  { key: 'rams', label: 'RAMS', dbField: 'ramsCompleted', priority: 'high' },
];

export const DocumentChecklist = memo(function DocumentChecklist({
  installationId,
  documents,
  compact = false
}: DocumentChecklistProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();

  // Local optimistic state for instant UI updates
  const [optimisticDocs, setOptimisticDocs] = useState<Partial<DocumentStatus>>({});

  // Clear optimistic state when server data arrives (documents prop changes)
  useEffect(() => {
    setOptimisticDocs({});
  }, [documents]);

  // Merge optimistic state with actual data for instant feedback
  // Defensive fallback: ensure documents is always a valid object to prevent iteration errors
  const safeDocuments: DocumentStatus = documents || {
    invoicePaid: false,
    evcpApplication: false,
    g99Commissioning: false,
    mcsCert: false,
    eicCert: false,
    partP: false,
    ownersManual: false,
    sld: false,
    datasheets: false,
    invoices: false,
    rams: false,
  };
  const mergedDocuments = { ...safeDocuments, ...optimisticDocs };

  // Calculate progress using merged documents for instant UI updates
  const completedCount = Object.values(mergedDocuments).filter(Boolean).length;
  const totalCount = Object.keys(mergedDocuments).length;
  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  // Update mutation with optimistic updates
  const updateDocument = useMutation({
    mutationFn: async ({ dbField, value, key }: { dbField: string; value: boolean; key: keyof DocumentStatus }) => {
      // RAMS field is boolean, others use 'completed'/'pending' text
      const fieldValue = dbField === 'ramsCompleted' ? value : (value ? 'completed' : 'pending');
      return apiRequest({
        method: 'PATCH',
        url: `/api/installations/${installationId}`,
        data: { [dbField]: fieldValue },
      });
    },
    onMutate: async ({ key, value }) => {
      // Optimistically update local state immediately
      setOptimisticDocs(prev => ({ ...prev, [key]: value }));
    },
    onSuccess: () => {
      // Only invalidate queries - optimistic state will clear when documents prop updates
      queryClient.invalidateQueries({ queryKey: ['/api/installations/grid-view'] });
      queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
    },
    onError: (error: Error, { key }) => {
      // Revert optimistic update on error
      setOptimisticDocs(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update document status',
        variant: 'destructive',
      });
    },
  });

  const handleToggle = (item: DocumentItem) => {
    const currentValue = mergedDocuments[item.key];
    updateDocument.mutate({ dbField: item.dbField, value: !currentValue, key: item.key });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full mt-2">
      <CollapsibleTrigger
        className="flex items-center justify-between w-full hover:bg-muted/50 rounded px-2 py-1 transition-colors"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FileCheck className={cn("flex-shrink-0 text-muted-foreground", compact ? "h-3 w-3" : "h-4 w-4")} />
          <div className="flex-1 min-w-0">
            <div className={cn("font-medium truncate", compact ? "text-[10px]" : "text-xs")}>
              Docs: {completedCount}/{totalCount}
            </div>
            <Progress
              value={progressPercentage}
              className={cn("mt-1", compact ? "h-1" : "h-1.5")}
            />
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className={cn("flex-shrink-0 text-muted-foreground ml-1", compact ? "h-3 w-3" : "h-4 w-4")} />
        ) : (
          <ChevronDown className={cn("flex-shrink-0 text-muted-foreground ml-1", compact ? "h-3 w-3" : "h-4 w-4")} />
        )}
      </CollapsibleTrigger>

      <CollapsibleContent className={cn("mt-2 space-y-1", compact ? "px-1" : "px-2")}>
        {DOCUMENT_ITEMS.map((item) => {
          const isComplete = mergedDocuments[item.key];

          return (
            <div
              key={item.key}
              className={cn(
                "flex items-center gap-2 rounded transition-colors",
                compact ? "py-0.5" : "py-1 px-1 hover:bg-muted/50"
              )}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Status indicator dot */}
              <Circle
                className={cn(
                  "flex-shrink-0",
                  compact ? "h-1.5 w-1.5" : "h-2 w-2",
                  isComplete ? "fill-green-500 text-green-500" :
                  item.priority === 'high' ? "fill-red-500 text-red-500" :
                  item.priority === 'medium' ? "fill-amber-500 text-amber-500" :
                  "fill-gray-300 text-gray-300"
                )}
              />

              {/* Checkbox */}
              <Checkbox
                id={`doc-${installationId}-${item.key}`}
                checked={isComplete}
                onCheckedChange={() => handleToggle(item)}
                disabled={updateDocument.isPending}
                className={cn(compact ? "h-3 w-3" : "h-4 w-4")}
                onClick={(e) => e.stopPropagation()}
              />

              {/* Label */}
              <label
                htmlFor={`doc-${installationId}-${item.key}`}
                className={cn(
                  "flex-1 cursor-pointer truncate",
                  compact ? "text-[10px]" : "text-xs",
                  isComplete ? "text-muted-foreground line-through" : "text-foreground"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                {item.label}
              </label>
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
});
